import { isEmpty } from 'lodash';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../../common/auth/UserContext';
import { fetchTimelineUpdates } from '../../../scheduler/fetchTimelineUpdates';
import {
    reconcileReservationTimelineUpdates,
    replaceTimelineEvents,
    type ApplyTimelineEventChanges
} from '../../../scheduler/timelineUpdates';
import { useConfigReservationScheduleRefetchNeeded } from '../../../common/utils/atoms/useConfigReservationScheduleRefetchNeeded';
import { failedToastMessage, successToastMessage } from '../../../common/utils/commonUtils';
import { getFirstDayOftheMonth } from '../../../common/utils/dateTime';
import {
    activitySearchByPage,
    productSearchByPage,
    reservationSearchPage
} from '../../../common/utils/incrementalSearchPages';
import type { SingleFilter } from '../../../common/utils/incrementalSearchPages';
import {
    dedupeRecordsById,
    getCachedData,
    PRODUCT_DEFAULT_FILTERS,
    toBryntumFormat,
    UpdateIDB
} from '../../../scheduler/scheduler';
import {
    ACTIVITY_CHUNK_SIZE,
    RESERVATION_CHUNK_SIZE,
    SCHEDULER_INDEX_DB_STATUS
} from '../../../scheduler/schedulerConstants';
import {
    createSchedulerStore,
    getAssignmentKey,
    getCalendars,
    getHoursSince,
    getSchedulerStoreDelta,
    getUniqueAssignments,
    mergeAssignments,
    productToBryntumResourcesAndCalendars,
    toBryntumCalendars,
    toBryntumEventsAndAssignmentsForActivity,
    toLineItemsFromProductionActivity,
    toSchedulerLineItemsFromReservations,
    toValidDate,
    updateEventsForRenderingOnTimeline
} from '../../../scheduler/schedulerUtil';

type ReservationSchedulerDataContextType = {
    batchReservationData: any;
    schedulerStoreData: any; // need to define types for this
    batchSchedulerStoreData: any;
    productData: any;
    dataLoaded: boolean;
    fetchUpdatedReservations: () => Promise<void>;
    applyTimelineEventChanges: ApplyTimelineEventChanges;
    isInitialized: boolean;
    isEnabled: boolean;
    ensureInitialized: () => void;
};

export const ReservationSchedulerDataContext = createContext<ReservationSchedulerDataContextType>(undefined);

export const useReservationSchedulerData = () => {
    const context = useContext(ReservationSchedulerDataContext);
    const ensureInitialized = context?.ensureInitialized;

    useEffect(() => {
        ensureInitialized?.();
    }, [ensureInitialized]);

    return context;
};

/** fetches reservations since the last updated date  */
export const getLastestReservationData = (activeOrganizationAccount, lastUpdatedDate, signal?: AbortSignal) => {
    const reservationUpdates = fetchTimelineUpdates('Reservation', activeOrganizationAccount, lastUpdatedDate, signal);
    return reservationUpdates;
};

export const ReservationSchedulerDataProvider = ({ children }) => {
    const { activeOrganizationAccount, application } = useContext(UserContext);
    const [schedulerStoreData, setSchedulerStoreData] = useState(undefined);
    const [productData, setProductData] = useState(undefined);
    const [batchProductData, setBatchProductData] = useState(undefined);
    const [calendarData, setCalendarData] = useState(undefined);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [batchReservationData, setBatchReservationData] = useState([]);
    const [batchActivityData, setBatchActivityData] = useState([]);
    const [batchSchedulerStoreData, setBatchSchedulerStoreData] = useState(undefined);
    const [isAllDataLoaded, setIsAllDataLoaded] = useState(false);
    const [lastFetchedAt, setLastFetchedAt] = useState(undefined);
    const schedulerPreviewStore = 'schedulerPreviewData';
    const [isSchedulerReservationRefetchNeeded] = useConfigReservationScheduleRefetchNeeded();
    const [isInitialized, setIsInitialized] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const schedulerStoreDataRef = useRef<any>(undefined);
    const eventMutationRevisionsRef = useRef(new Map<string, number>());
    const schedulerOwnerRef = useRef<string | undefined>(undefined);
    const refreshPromiseRef = useRef<Promise<void> | undefined>(undefined);

    const resetStates = useCallback(() => {
        setDataLoaded(false);
        setIsAllDataLoaded(false);
        setCalendarData(undefined);
        setProductData(undefined);
        setBatchProductData(undefined);
        setBatchReservationData([]);
        setBatchActivityData([]);
        setBatchSchedulerStoreData(undefined);
        setSchedulerStoreData(undefined);
        setLastFetchedAt(undefined);
        setIsInitialized(false);
        schedulerStoreDataRef.current = undefined;
        eventMutationRevisionsRef.current = new Map();
    }, []);

    const ensureInitialized = useCallback(() => {
        setIsEnabled(true);
    }, []);

    const applyTimelineEventChanges: ApplyTimelineEventChanges = useCallback(
        async (updates, eventIds) => {
            if (!mountedRef.current || schedulerOwnerRef.current !== activeOrganizationAccount) return;
            // Record mutations even when no cached event exists, before a pending refresh can commit.
            for (const eventId of new Set(eventIds)) {
                const revision = eventMutationRevisionsRef.current.get(eventId) ?? 0;
                eventMutationRevisionsRef.current.set(eventId, revision + 1);
            }
            const nextData = replaceTimelineEvents(schedulerStoreDataRef.current ?? {}, updates, eventIds);
            // Refresh reconciliation must see SSE edits, including deletions, before its request finishes.
            schedulerStoreDataRef.current = nextData;
            setSchedulerStoreData(nextData);
            await UpdateIDB(activeOrganizationAccount, schedulerPreviewStore, updates, eventIds);
        },
        [activeOrganizationAccount]
    );

    const buildProductSnapshot = useCallback((products = []) => {
        const { resources } = productToBryntumResourcesAndCalendars(products);
        return createSchedulerStore([], {
            resources: dedupeRecordsById(resources || [])
        });
    }, []);

    const buildReservationSnapshot = useCallback((reservations = []) => {
        const { events, assignments } = toBryntumFormat(reservations, [], []);
        return createSchedulerStore([], {
            events: dedupeRecordsById(events || []),
            assignments: getUniqueAssignments(
                assignments || [],
                (assignment) => Boolean(getAssignmentKey(assignment)),
                (assignment) => getAssignmentKey(assignment)
            )
        });
    }, []);

    const buildActivitySnapshot = useCallback(
        (activities = []) => {
            const lineItems = toLineItemsFromProductionActivity(activities, application.name);
            const { events, assignments } = toBryntumEventsAndAssignmentsForActivity(lineItems, application);

            return createSchedulerStore([], {
                events: dedupeRecordsById(events || []),
                assignments: getUniqueAssignments(
                    assignments || [],
                    (assignment) => Boolean(getAssignmentKey(assignment)),
                    (assignment) => getAssignmentKey(assignment)
                )
            });
        },
        [application]
    );

    const applySchedulerSnapshot = useCallback(
        (snapshot, overrides: Record<string, any> = {}) => {
            const currentStoreData = schedulerStoreDataRef.current || createSchedulerStore(calendarData || []);
            const deltaStoreData = getSchedulerStoreDelta(currentStoreData, snapshot);
            const mergedStoreData = {
                ...currentStoreData,
                ...overrides,
                events: dedupeRecordsById([...(currentStoreData.events || []), ...(deltaStoreData.events || [])]),
                resources: dedupeRecordsById([
                    ...(currentStoreData.resources || []),
                    ...(deltaStoreData.resources || [])
                ]),
                assignments: mergeAssignments(currentStoreData.assignments || [], deltaStoreData.assignments || []),
                calendars: dedupeRecordsById([
                    ...(currentStoreData.calendars || []),
                    ...((snapshot?.calendars || []).length ? snapshot.calendars : calendarData || [])
                ])
            };

            const hasDelta =
                deltaStoreData.events.length > 0 ||
                deltaStoreData.resources.length > 0 ||
                deltaStoreData.assignments.length > 0;

            const hasFetchTimestampOverride =
                overrides.lastFetchedAt !== undefined || overrides.eventsFetchedAt !== undefined;

            if (!hasDelta && !hasFetchTimestampOverride) {
                return;
            }

            schedulerStoreDataRef.current = mergedStoreData;
            setSchedulerStoreData(mergedStoreData);

            const shouldReplayMergedSnapshot =
                !(currentStoreData.resources || []).length && (mergedStoreData.resources || []).length > 0;
            setBatchSchedulerStoreData(shouldReplayMergedSnapshot ? mergedStoreData : deltaStoreData);
            UpdateIDB(activeOrganizationAccount, schedulerPreviewStore, mergedStoreData);
        },
        [activeOrganizationAccount, calendarData]
    );

    const getData = useCallback(
        async (
            schedulerStoreData: any,
            signal?: AbortSignal,
            includeActivities = false
        ): Promise<{
            updates: any;
            results: any[];
            requestedMutationRevisions: ReadonlyMap<string, number>;
        }> => {
            const requestedMutationRevisions = new Map(eventMutationRevisionsRef.current);
            const fetchedAt = new Date();
            const [reservations, activities] = await Promise.all([
                getLastestReservationData(activeOrganizationAccount, schedulerStoreData.lastFetchedAt, signal),
                includeActivities
                    ? fetchTimelineUpdates(
                          'Activity',
                          activeOrganizationAccount,
                          schedulerStoreData.lastFetchedAt,
                          signal,
                          'ProductReferences'
                      )
                    : Promise.resolve([])
            ]);
            const results = [...reservations, ...activities];
            let updates = {};
            if (results.length) {
                successToastMessage('timeline.refetch.message', { count: results.length });
                const schedulerLineItems = toSchedulerLineItemsFromReservations(reservations);
                const { events, assignments } = updateEventsForRenderingOnTimeline(schedulerLineItems);
                const activitySnapshot = buildActivitySnapshot(activities);
                updates = {
                    events: [...events, ...activitySnapshot.events],
                    assignments: [...assignments, ...activitySnapshot.assignments],
                    lastFetchedAt: fetchedAt
                };
            }
            signal?.throwIfAborted();
            const latestSchedulerData = { updates, results, requestedMutationRevisions };
            return latestSchedulerData;
        },
        [activeOrganizationAccount, buildActivitySnapshot]
    );

    const commitReservationUpdates = useCallback(
        async (requestedData, updates, results: any[], requestedMutationRevisions: ReadonlyMap<string, number>) => {
            const eventIds = results.map((item) => item.id);
            const reconciledData = reconcileReservationTimelineUpdates(
                requestedData,
                schedulerStoreDataRef.current,
                updates,
                eventIds,
                requestedMutationRevisions,
                eventMutationRevisionsRef.current
            );
            schedulerStoreDataRef.current = reconciledData.formattedData;
            setSchedulerStoreData(reconciledData.formattedData);
            const batchSchedulerData = {
                ...reconciledData.formattedData,
                replaceEventIds: reconciledData.replaceEventIds
            };
            setBatchSchedulerStoreData(batchSchedulerData);
            if (results.length) {
                await UpdateIDB(
                    activeOrganizationAccount,
                    schedulerPreviewStore,
                    reconciledData.updates,
                    reconciledData.replaceEventIds
                );
            }
        },
        [activeOrganizationAccount]
    );

    useEffect(() => {
        if (!isEnabled || !activeOrganizationAccount || !application) {
            return;
        }

        resetStates();
        schedulerOwnerRef.current = activeOrganizationAccount;
        refreshPromiseRef.current = undefined;
        mountedRef.current = true;
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        const signal = abortRef.current.signal;
        getCachedData(activeOrganizationAccount, schedulerPreviewStore).then((data) => {
            if (!mountedRef.current || signal.aborted) return;
            const isCacheEmpty = isEmpty(data);
            const lastFetchedAt = toValidDate(data?.lastFetchedAt);
            const hoursSinceLastFetch = getHoursSince(lastFetchedAt);
            const isCacheStale = !isCacheEmpty && (hoursSinceLastFetch == null || hoursSinceLastFetch > 1);
            const hasNoEvents = !isCacheEmpty && isEmpty(data?.events);
            const eventsFetchedAt = toValidDate(data?.eventsFetchedAt);
            const eventsNeverFetched = !isCacheEmpty && !eventsFetchedAt;

            const reservationFetchState = data?.fetchMeta?.state;
            const hasFetchMeta = Boolean(data?.fetchMeta);
            const isReservationFetchComplete = reservationFetchState === SCHEDULER_INDEX_DB_STATUS.complete;

            const productFilters: SingleFilter[] = [{ filters: PRODUCT_DEFAULT_FILTERS }];

            const shouldRefetch =
                isCacheEmpty ||
                isCacheStale ||
                (hasNoEvents && eventsNeverFetched) ||
                (hasFetchMeta && !isReservationFetchComplete);

            if (shouldRefetch) {
                if (!mountedRef.current || signal.aborted) return;
                UpdateIDB(activeOrganizationAccount, schedulerPreviewStore, {
                    fetchMeta: {
                        ...(data?.fetchMeta || {}),
                        state: SCHEDULER_INDEX_DB_STATUS.in_progress,
                        updatedAt: new Date()
                    }
                });

                const calendarPromise = getCalendars(activeOrganizationAccount);
                Promise.all([calendarPromise]).then((res) => {
                    if (!mountedRef.current || signal.aborted) return;

                    const calendarData = res[0].data?.results?.hits?.items || [];
                    setCalendarData(toBryntumCalendars(calendarData));

                    const date = getFirstDayOftheMonth();
                    const filters: SingleFilter[] = [
                        { filters: [{ identifier: 'enddate', dateMin: date }], sortBy: 'enddate_ASC' },
                        {
                            filters: [{ identifier: 'enddate', dateMax: date, maxValueExcluded: true }],
                            sortBy: 'enddate_DESC'
                        }
                    ];
                    setLastFetchedAt(new Date());
                    const productsPromise = Promise.all(
                        productSearchByPage(
                            activeOrganizationAccount,
                            200,
                            productFilters,
                            setBatchProductData,
                            setProductData,
                            2,
                            false,
                            undefined,
                            signal
                        )
                    ).then((productResults) => {
                        if (!mountedRef.current || signal.aborted) return;
                        const productsData = productResults.flat();
                        setProductData(productsData);
                    });
                    const reservationPromises = Promise.all(
                        reservationSearchPage(
                            activeOrganizationAccount,
                            200,
                            filters,
                            setBatchReservationData,
                            undefined,
                            RESERVATION_CHUNK_SIZE,
                            false,
                            undefined,
                            signal
                        )
                    );

                    const activityPromises = Promise.all(
                        activitySearchByPage(
                            activeOrganizationAccount,
                            200,
                            filters,
                            setBatchActivityData,
                            undefined,
                            ACTIVITY_CHUNK_SIZE,
                            true,
                            'ProductReferences',
                            signal
                        )
                    );

                    Promise.all([productsPromise, reservationPromises, activityPromises]).then(() => {
                        if (!mountedRef.current || signal.aborted) return;
                        setDataLoaded(true);
                        setIsAllDataLoaded(true);
                    });
                });
            } else {
                setProductData(data.resources);
                setIsAllDataLoaded(true);
                const loadCachedSchedulerData = async () => {
                    try {
                        const latestSchedulerData = await getData(data, signal, true);
                        if (!mountedRef.current || signal.aborted) return;
                        await commitReservationUpdates(
                            data,
                            latestSchedulerData.updates,
                            latestSchedulerData.results,
                            latestSchedulerData.requestedMutationRevisions
                        );
                    } catch (error) {
                        if (!mountedRef.current || signal.aborted) return;
                        console.error('Error refreshing cached RM timeline:', error);
                        failedToastMessage('form.load.failed.message');
                        await commitReservationUpdates(data, {}, [], eventMutationRevisionsRef.current);
                    }
                    if (!mountedRef.current || signal.aborted) return;
                    setIsInitialized(true);
                    setDataLoaded(true);
                };
                loadCachedSchedulerData();
            }
        });

        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
        };
    }, [isEnabled, activeOrganizationAccount, application, commitReservationUpdates, getData, resetStates]);

    useEffect(() => {
        if (Array.isArray(batchProductData) && batchProductData.length) {
            applySchedulerSnapshot(buildProductSnapshot(batchProductData));
        }
    }, [applySchedulerSnapshot, batchProductData, buildProductSnapshot]);

    useEffect(() => {
        if (Array.isArray(batchReservationData) && batchReservationData.length) {
            applySchedulerSnapshot(buildReservationSnapshot(batchReservationData), {
                lastFetchedAt: lastFetchedAt || schedulerStoreDataRef.current?.lastFetchedAt || new Date(),
                eventsFetchedAt: new Date()
            });
        }
    }, [applySchedulerSnapshot, batchReservationData, buildReservationSnapshot, lastFetchedAt]);

    useEffect(() => {
        if (Array.isArray(batchActivityData) && batchActivityData.length) {
            applySchedulerSnapshot(buildActivitySnapshot(batchActivityData));
        }
    }, [applySchedulerSnapshot, batchActivityData, buildActivitySnapshot]);

    const fetchUpdatedReservations = useCallback((): Promise<void> => {
        if (
            !isEnabled ||
            !schedulerStoreDataRef.current ||
            !isAllDataLoaded ||
            schedulerOwnerRef.current !== activeOrganizationAccount
        ) {
            const completedRefresh = Promise.resolve();
            return completedRefresh;
        }
        if (refreshPromiseRef.current) return refreshPromiseRef.current;
        const signal = abortRef.current?.signal;
        const refreshTimelineUpdates = async () => {
            try {
                const requestedData = schedulerStoreDataRef.current;
                const data = await getData(requestedData, signal, true);
                if (!mountedRef.current || signal?.aborted) return;
                if (data?.results?.length) {
                    await commitReservationUpdates(
                        requestedData,
                        data.updates,
                        data.results,
                        data.requestedMutationRevisions
                    );
                }
            } catch (error) {
                if (!mountedRef.current || signal?.aborted) return;
                console.error('Error refreshing RM timeline:', error);
                failedToastMessage('form.load.failed.message');
            } finally {
                if (refreshPromiseRef.current === refresh) refreshPromiseRef.current = undefined;
            }
        };
        const refresh = refreshTimelineUpdates();
        refreshPromiseRef.current = refresh;
        return refresh;
    }, [activeOrganizationAccount, commitReservationUpdates, getData, isAllDataLoaded, isEnabled]);

    useEffect(() => {
        if (isSchedulerReservationRefetchNeeded) {
            getCachedData(activeOrganizationAccount, schedulerPreviewStore).then((data) => {
                if (!isEmpty(data)) {
                    const currentData = schedulerStoreDataRef.current;
                    if (!currentData) return;
                    const nextStoreData = {
                        ...currentData,
                        assignments: [...data.assignments],
                        events: [...data.events]
                    };
                    schedulerStoreDataRef.current = nextStoreData;
                    setSchedulerStoreData(nextStoreData);
                }
            });
        }
    }, [isSchedulerReservationRefetchNeeded, activeOrganizationAccount]);

    useEffect(() => {
        if (!mountedRef.current || abortRef.current?.signal?.aborted) return;
        if (schedulerStoreData && dataLoaded) {
            const timeout = setTimeout(() => {
                if (!mountedRef.current || abortRef.current?.signal?.aborted) return;
                UpdateIDB(activeOrganizationAccount, schedulerPreviewStore, {
                    ...schedulerStoreData,
                    fetchMeta: {
                        ...(schedulerStoreData?.fetchMeta || {}),
                        state: SCHEDULER_INDEX_DB_STATUS.complete,
                        completedAt: new Date(),
                        updatedAt: new Date()
                    }
                });
                setIsInitialized(true);
            }, 1000);

            return () => clearTimeout(timeout);
        }
    }, [dataLoaded, schedulerStoreData, activeOrganizationAccount]);

    return (
        <ReservationSchedulerDataContext.Provider
            value={{
                batchReservationData,
                schedulerStoreData,
                productData,
                batchSchedulerStoreData,
                dataLoaded,
                fetchUpdatedReservations,
                applyTimelineEventChanges,
                isInitialized,
                isEnabled,
                ensureInitialized
            }}
        >
            {children}
        </ReservationSchedulerDataContext.Provider>
    );
};
